import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, TableCell } from '@mui/material';

import { Project } from 'services/projects';
import {
  ItemsTable,
  Column,
  ColumnTypes,
  useSelectedItemsChanges,
} from 'ui/components/Table/ItemsTable';
import {
  deleteProjectById,
  getProjectsByCustomerId,
  postProject,
  putProject,
} from 'services/projects/api';
import { initialProject } from 'services/projects/consts';
import { showNotification } from 'services/api';
import { getErrorMessage } from 'helpers';
import { logErrorCtx } from 'app/logging';
import { PermissionType } from 'services/permissions';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import { ConfirmationModal } from 'ui/components/Modal/ConfirmationModal';
import { IconNames } from 'ui/theme';
import FBOTitleBar from 'ui/theme/components/FBOTitleBar/FBOTitleBar';

import { ProjectsTabProps } from './types';
import ProjectModal from './ProjectModal';

const ProjectsTab: React.FC<ProjectsTabProps> = (props) => {
  const { activeCustomer } = props;

  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);
  const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedProjects,
    setSelectedProjects
  );

  const fetchProjects = useCallback(async () => {
    if (!activeCustomer.id || activeCustomer.id === -1) {
      setProjects([]);
      return;
    }

    setIsLoading(true);
    try {
      const customerProjects = await getProjectsByCustomerId(activeCustomer.id);
      setProjects(customerProjects);
    } catch (e) {
      const message = getErrorMessage(e);
      logErrorCtx('Error while loading Projects', {
        error: e as Error,
        description: message,
        component: 'ProjectsTab',
      });
      showNotification(`${message} - Projects could not be loaded.`, {
        variant: 'error',
      });
    }
    setIsLoading(false);
  }, [activeCustomer.id]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleAddProject = useCallback(() => {
    setSelectedProject({
      ...initialProject,
      customerId: activeCustomer.id,
      customer: activeCustomer.id
        ? {
            id: activeCustomer.id,
            name: activeCustomer.name || '',
          }
        : null,
    });
    setProjectModalOpen(true);
  }, [activeCustomer]);

  const handleRowClick = useCallback((project: Project) => {
    setSelectedProject(project);
    setProjectModalOpen(true);
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedProjects.length === 0) return;
    setBulkDeleteModalVisible(true);
  }, [selectedProjects.length]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (selectedProjects.length === 0) return;

    try {
      await Promise.all(
        selectedProjects.map((projectId) => deleteProjectById(projectId))
      );
      await fetchProjects();
      setSelectedProjects([]);
      setBulkDeleteModalVisible(false);

      const message =
        selectedProjects.length === 1
          ? 'Project successfully deleted'
          : 'Projects successfully deleted';
      showNotification(message, { variant: 'success' });
    } catch (error) {
      logErrorCtx('Error while deleting projects', {
        error: error as Error,
        component: 'ProjectsTab',
      });
      showNotification('Failed to delete projects', {
        variant: 'error',
      });
    }
  }, [selectedProjects, fetchProjects]);

  const handleSaveProject = useCallback(
    async (project: Project) => {
      try {
        if (!project.id || project.id === -1) {
          await postProject({
            ...project,
            customerId: activeCustomer.id,
          });
        } else {
          await putProject(project);
        }
        await fetchProjects();
        setProjectModalOpen(false);
        setSelectedProject(null);
      } catch (e) {
        const message = getErrorMessage(e);
        logErrorCtx('Error saving project', {
          error: e as Error,
          description: message,
          component: 'ProjectsTab',
        });
        // API already shows error notification, no need to show it again
      }
    },
    [activeCustomer.id, fetchProjects]
  );

  const handleCloseModal = useCallback(() => {
    setProjectModalOpen(false);
    setSelectedProject(null);
  }, []);

  const columns: Column[] = [
    {
      title: 'Name',
      width: 250,
      type: ColumnTypes.renderByCustomRow,
    },
    {
      title: 'Description',
      width: 'auto',
      type: ColumnTypes.renderByCustomRow,
    },
  ];

  const RenderCustomRow = useCallback(
    (rowProps: any) => {
      const { row } = rowProps;
      return (
        <>
          <TableCell
            style={{ cursor: 'pointer' }}
            onClick={() => handleRowClick(row)}
          >
            {row.name || '-'}
          </TableCell>
          <TableCell
            style={{ cursor: 'pointer' }}
            onClick={() => handleRowClick(row)}
          >
            {row.notes || '-'}
          </TableCell>
        </>
      );
    },
    [handleRowClick]
  );

  if (!activeCustomer.id || activeCustomer.id === -1) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        p={4}
      >
        <Typography variant="body1" color="textSecondary">
          Please save the customer first to manage projects.
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" width="100%">
      <FBOTitleBar title="Projects">
        {selectedProjects.length > 0 && (
          <FBOButton
            sx={{ marginRight: '8px' }}
            variant="secondary"
            color="negative"
            size="medium"
            icon={IconNames.FBODeleteSmall}
            data-qa="projects-bulk-delete"
            onClick={handleBulkDeleteClick}
            permissions={[PermissionType.AccountingEdit]}
          >
            Delete
          </FBOButton>
        )}
        <FBOButton
          variant="primary"
          color="positive"
          size="medium"
          onClick={handleAddProject}
          permissions={[PermissionType.AccountingEdit]}
          data-qa="customer-projects-add-new"
        >
          Add New
        </FBOButton>
      </FBOTitleBar>

      <ItemsTable
        data={projects}
        columns={columns}
        isLoading={isLoading}
        selectedItems={selectedProjects}
        onSelectedChange={handleSelectedChange}
        RenderCustomRow={RenderCustomRow}
      />

      {selectedProject && (
        <ProjectModal
          open={projectModalOpen}
          project={selectedProject}
          onClose={handleCloseModal}
          onSave={handleSaveProject}
        />
      )}

      <ConfirmationModal
        open={bulkDeleteModalVisible}
        title={
          selectedProjects.length === 1 ? 'Delete Project' : 'Delete Projects'
        }
        body={`Are you sure you want to delete the selected project${
          selectedProjects.length === 1 ? '' : 's'
        }? This action cannot be undone.`}
        onCancelClicked={() => setBulkDeleteModalVisible(false)}
        onConfirmClicked={handleBulkDeleteConfirm}
        confirmLabel="Delete"
        confirmButtonRed
      />
    </Box>
  );
};

export default ProjectsTab;
